export type AnalyticsArea =
  | "acquisition"
  | "activation"
  | "companion"
  | "community"
  | "crew"
  | "explore"
  | "marketplace"
  | "notifications"
  | "planner"
  | "reliability";

type AnalyticsEventDefinition = {
  area: AnalyticsArea;
  description: string;
  version: number;
};

function defineEvent(
  area: AnalyticsArea,
  description: string,
  version = 1,
): AnalyticsEventDefinition {
  return { area, description, version };
}

/**
 * Canonical PeakWave product-event registry.
 *
 * Every custom PostHog event must be declared here before it can be captured.
 * Versions change only when the meaning or payload contract changes.
 */
export const ANALYTICS_EVENT_REGISTRY = {
  conditions_window_action: defineEvent('explore', 'A visitor views, saves, shares or plans from a conditions window; action identifies the completed step.'),
  conditions_hub_sport_select: defineEvent('explore', 'A visitor picks a sport (or All) on the /conditions hub board; source is pin, sheet or search.'),
  conditions_hub_search: defineEvent('explore', 'A visitor opens a /conditions hub search result; carries query_length, result_type and position, never the query.'),
  signup_started: defineEvent(
    "activation",
    "Candidate makes the first meaningful change in the signup form.",
    2,
  ),
  signup_details_submitted: defineEvent(
    "activation",
    "Candidate passes form validation and sends a new-account request to authentication.",
  ),
  signup_confirmation_pending: defineEvent(
    "activation",
    "Authentication accepts the candidate account request and email confirmation is pending.",
  ),
  signup_confirmation_resent: defineEvent(
    "activation",
    "Authentication accepts a request to resend the candidate confirmation email.",
  ),
  signup_email_confirmed: defineEvent(
    "activation",
    "The authenticated callback verifies the candidate email and finds the pending signup.",
  ),
  signup_completed: defineEvent(
    "activation",
    "Candidate account and profile creation completes successfully.",
    2,
  ),
  employer_signup_started: defineEvent(
    "activation",
    "Employer makes the first meaningful change in the signup form.",
  ),
  employer_signup_details_submitted: defineEvent(
    "activation", "Employer passes validation and submits account details.",
  ),
  employer_signup_confirmation_pending: defineEvent(
    "activation", "Authentication accepts the employer request; email confirmation is pending.",
  ),
  employer_signup_confirmation_resent: defineEvent(
    "activation", "Authentication accepts an employer confirmation email resend.",
  ),
  employer_signup_completed: defineEvent(
    "activation",
    "Employer membership creation succeeds; signup_mode separates new company from invite.",
    3,
  ),
  employer_listing_published: defineEvent(
    "marketplace", "A new active employer listing is saved; source separates signup from listing form.",
  ),
  employer_claim_completed: defineEvent(
    "activation", "An employer claims a company whose listing an admin posted on their behalf; source names the flow.",
  ),
  signup_pending_upsert_failed: defineEvent(
    "reliability",
    "Candidate signup draft backup fails without blocking signup.",
    2,
  ),
  signup_crash: defineEvent(
    "reliability",
    "Candidate signup error boundary is shown.",
    2,
  ),
  dashboard_crash: defineEvent(
    "reliability",
    "Candidate dashboard error boundary is shown.",
    2,
  ),
  partnership_application_submitted: defineEvent(
    "acquisition",
    "Partnership enquiry is submitted.",
  ),
  referral_link_copied: defineEvent(
    "acquisition",
    "Candidate copies a PeakWave referral link.",
  ),
  message_sent: defineEvent(
    "marketplace",
    "A new marketplace conversation or message is sent.",
  ),
  message_replied: defineEvent(
    "marketplace",
    "A participant replies in an existing marketplace conversation.",
  ),
  conversation_declined: defineEvent(
    "marketplace",
    "A marketplace conversation is declined.",
  ),
  shortlist_toggled: defineEvent(
    "marketplace",
    "An employer changes a candidate shortlist state.",
  ),
  shortlist_removed: defineEvent(
    "marketplace",
    "An employer removes a candidate from the shortlist.",
  ),
  filter_applied: defineEvent(
    "marketplace",
    "A marketplace results filter is applied.",
    2,
  ),
  job_alerts_toggled: defineEvent(
    "notifications",
    "A candidate enables or disables marketplace job alerts.",
  ),
  listing_interest_registered: defineEvent(
    "marketplace",
    "A candidate registers interest in a listing.",
  ),
  listing_viewed: defineEvent(
    "marketplace",
    "A listing detail page is viewed.",
  ),
  candidate_profile_viewed: defineEvent(
    "marketplace",
    "An employer views a candidate profile.",
  ),
  listing_saved: defineEvent(
    "marketplace",
    "A candidate saves a listing.",
  ),
  listing_unsaved: defineEvent(
    "marketplace",
    "A candidate removes a saved listing.",
  ),
  placement_requested: defineEvent(
    "marketplace",
    "An employer requests confirmation of a placement.",
  ),
  placement_confirmed: defineEvent(
    "marketplace",
    "A candidate confirms a placement.",
  ),
  employer_job_page_admin_action: defineEvent(
    "marketplace",
    "An administrator changes an employer job page.",
  ),
  designated_employer_card_impression: defineEvent(
    "marketplace",
    "A designated employer card becomes available in the jobs marketplace.",
  ),
  designated_employer_card_clicked: defineEvent(
    "marketplace",
    "A designated employer card is opened.",
  ),
  designated_job_detail_clicked: defineEvent(
    "marketplace",
    "A designated employer listing detail is opened.",
  ),
  content_loop_cta: defineEvent(
    "acquisition",
    "A marketplace content-loop call to action is selected.",
  ),
  job_match_alert_sent: defineEvent(
    "notifications",
    "A durable candidate job-match digest is accepted by the email provider.",
    2,
  ),
  candidate_match_alert_sent: defineEvent(
    "notifications",
    "A durable employer candidate-match digest is accepted by the email provider.",
    2,
  ),
  crew_joined: defineEvent("crew", "A candidate joins a crew."),
  crew_left: defineEvent("crew", "A candidate leaves a crew."),
  crew_confirmed: defineEvent(
    "crew",
    "A crew or placement confirmation is completed.",
  ),
  crew_chat_message_sent: defineEvent(
    "crew",
    "A crew chat message is sent.",
  ),
  crew_chat_message_reported: defineEvent(
    "crew",
    "A crew chat message is reported.",
  ),
  crew_page_viewed: defineEvent("crew", "A crew page is viewed."),
  crew_directory_viewed: defineEvent(
    "crew",
    "The crew directory is viewed or filtered.",
  ),
  companion_viewed: defineEvent(
    "companion",
    "A Companion destination is viewed.",
  ),
  companion_landing_viewed: defineEvent("companion", "The public Companion introduction is viewed."),
  companion_signup_opened: defineEvent("activation", "A visitor opens signup from the Companion introduction."),
  companion_sport_saved: defineEvent("activation", "A Companion sport is saved during setup."),
  companion_setup_progress: defineEvent("activation", "Companion setup shows whether a base and sports are saved."),
  recommendation_viewed: defineEvent(
    "companion",
    "A Companion recommendation is viewed.",
  ),
  recommendation_actioned: defineEvent(
    "companion",
    "A Companion recommendation action is selected.",
  ),
  companion_destination_opened: defineEvent(
    "companion",
    "A Companion destination is opened.",
  ),
  companion_section_load_failed: defineEvent(
    "reliability",
    "A Companion section cannot load.",
  ),
  companion_section_retry: defineEvent(
    "reliability",
    "A Companion section retry is attempted.",
  ),
  discovery_viewed: defineEvent(
    "companion",
    "A Companion discovery recommendation is viewed.",
  ),
  discovery_actioned: defineEvent(
    "companion",
    "A Companion discovery recommendation is selected.",
  ),
  roll_call_updated: defineEvent(
    "community",
    "A candidate updates a roll-call response.",
  ),
  meetup_created: defineEvent("community", "A community meetup is created."),
  meetup_rsvp_updated: defineEvent(
    "community",
    "A community meetup RSVP is changed.",
  ),
  status_pin_updated: defineEvent(
    "community",
    "A community status pin is changed.",
  ),
  social_moment_opened: defineEvent(
    "community",
    "A community social moment is opened.",
  ),
  steeze_toggled: defineEvent(
    "community",
    "A community steeze reaction is changed.",
  ),
  companion_people_section_failed: defineEvent(
    "reliability",
    "A Companion people-section source fails.",
  ),
  event_submission_completed: defineEvent(
    "community",
    "A community event submission completes.",
  ),
  event_organiser_created: defineEvent(
    "community",
    "A signed-in member creates an event organiser identity.",
  ),
  event_published: defineEvent(
    "community",
    "An authorised organiser publishes one event or a recurring event series.",
  ),
  event_updated: defineEvent(
    "community",
    "An authorised organiser updates or restores a published event.",
  ),
  event_cancelled: defineEvent(
    "community",
    "An authorised organiser cancels a published event.",
  ),
  event_response_updated: defineEvent(
    "community",
    "A signed-in member changes their Save, Going or reminder choice.",
  ),
  event_detail_viewed: defineEvent(
    "community",
    "A public event detail page records a session-deduplicated view.",
  ),
  event_source_opened: defineEvent(
    "community",
    "A public event source link is opened.",
  ),
  event_calendar_exported: defineEvent(
    "community",
    "A public event calendar file is requested.",
  ),
  forum_recommendation_opened: defineEvent(
    "community",
    "A recommended forum discussion is opened.",
  ),
  event_opened: defineEvent(
    "community",
    "A recommended community event is opened.",
  ),
  record_opened: defineEvent(
    "companion",
    "The Companion Record destination is opened.",
  ),
  tracking_started: defineEvent(
    "companion",
    "A user opens tracking from Companion Record; this is not recording or location consent.",
  ),
  piste_map_geometry_loaded: defineEvent(
    "companion",
    "A verified mountain geometry payload loads online or from an explicit offline cache.",
  ),
  piste_map_geometry_failed: defineEvent(
    "reliability",
    "The tracker cannot load any verified mountain geometry candidate.",
  ),
  piste_map_resort_detection: defineEvent(
    "companion",
    "The tracker resolves a privacy-safe count of nearby verified mountain candidates.",
  ),
  piste_map_tracker_fallback: defineEvent(
    "reliability",
    "The tracker uses a later resort candidate or saved map after its preferred source fails.",
  ),
  piste_map_action: defineEvent(
    "companion",
    "A user searches or selects a mountain-map safety and navigation action.",
  ),
  manual_log_started: defineEvent(
    "companion",
    "A manual Companion activity log starts.",
  ),
  manual_log_completed: defineEvent("companion", "The server accepts a user-submitted manual activity log; activity is self-reported."),
  planner_generation_started: defineEvent("planner", "A user requests a route with their current settings."),
  planner_generation_succeeded: defineEvent("planner", "A non-empty route is generated and displayed."),
  planner_generation_failed: defineEvent("planner", "A route request fails, times out or finds no suitable route."),
  planner_route_saved: defineEvent("planner", "The chosen route is successfully saved on this device."),
  planner_route_reopened: defineEvent("planner", "An explicit saved-route action or link restores the matching device route for review."),
  planner_route_started: defineEvent("planner", "The user explicitly starts their saved route."),
  planner_route_completed: defineEvent("planner", "The user marks their route complete."),
  planner_route_feedback: defineEvent("planner", "The user saves an explicit would-ride-again response for a completed route, with optional fixed-choice reasons and timing provenance.", 2),
  planner_style_selected: defineEvent(
    "planner",
    "A planner day style is selected.",
  ),
  planner_route_b_swapped: defineEvent(
    "planner",
    "The planner switches to an alternative route.",
  ),
  planner_leg_swapped: defineEvent(
    "planner",
    "A planner route leg is replaced.",
  ),
  planner_stop_edited: defineEvent(
    "planner",
    "A planner stop is added, changed or removed.",
  ),
  planner_replanned: defineEvent(
    "planner",
    "The planner generates a revised plan.",
  ),
  planner_plan_abandoned: defineEvent(
    "planner",
    "Legacy page-exit event. Retained for historical reports; the planner no longer emits it.",
  ),
  planner_edited: defineEvent(
    "planner",
    "A legacy planner edit is completed.",
  ),
  mountain_contribution_opened: defineEvent(
    "community",
    "The mountain contribution flow is opened.",
    2,
  ),
  mountain_contribution_submitted: defineEvent(
    "community",
    "A mountain contribution is submitted.",
    2,
  ),
  mountain_comment_reported: defineEvent(
    "community",
    "A mountain contribution comment is reported.",
    2,
  ),
  mountain_comment_moderated: defineEvent(
    "community",
    "A mountain contribution comment is moderated.",
    2,
  ),
  photo_submission_started: defineEvent(
    "explore",
    "An Explore photo submission starts.",
  ),
  photo_submission_completed: defineEvent(
    "explore",
    "An Explore photo submission completes.",
  ),
  photo_submission_failed: defineEvent(
    "reliability",
    "An Explore photo submission fails.",
  ),
  explore_hub_season_card_clicked: defineEvent(
    "explore",
    "An Explore season card is selected.",
  ),
  explore_hub_tools_clicked: defineEvent(
    "explore",
    "An Explore tool is selected.",
  ),
  explore_hub_featured_clicked: defineEvent(
    "explore",
    "An Explore featured item is selected.",
  ),
  explore_season_strip_clicked: defineEvent(
    "explore",
    "An Explore season-strip item is selected.",
  ),
  submit_your_own_clicked: defineEvent(
    "explore",
    "An Explore submission call to action is selected.",
  ),
  explore_tool_bridge_clicked: defineEvent(
    "explore",
    "An Explore bridge to a planning tool is selected.",
  ),
  feature_waitlist_signup: defineEvent(
    "explore",
    "A candidate joins an Explore feature waitlist.",
  ),
  related_in_explore_clicked: defineEvent(
    "explore",
    "A related Explore page is selected.",
  ),
  explore_nav_opened: defineEvent(
    "explore",
    "The Explore navigation is opened.",
  ),
  explore_breadcrumb_clicked: defineEvent(
    "explore",
    "An Explore breadcrumb is selected.",
  ),
  festival_quiz_started: defineEvent(
    "explore",
    "The festival quiz starts.",
  ),
  festival_quiz_completed: defineEvent(
    "explore",
    "The festival quiz completes.",
  ),
  festival_quiz_result_clicked: defineEvent(
    "explore",
    "A festival quiz result is opened.",
  ),
  festival_quiz_shared: defineEvent(
    "explore",
    "A festival quiz result is shared.",
  ),
  destination_quiz_started: defineEvent(
    "explore",
    "The destination quiz starts.",
  ),
  destination_quiz_completed: defineEvent(
    "explore",
    "The destination quiz completes.",
  ),
  destination_quiz_result_clicked: defineEvent(
    "explore",
    "A destination quiz result is opened.",
  ),
  destination_quiz_shared: defineEvent(
    "explore",
    "A destination quiz result is shared.",
  ),
} as const;

export type AnalyticsEventName = keyof typeof ANALYTICS_EVENT_REGISTRY;
export type AnalyticsSource = "client" | "server";
export type AnalyticsPrimitive = string | number | boolean | null;
export type AnalyticsPropertyValue =
  | AnalyticsPrimitive
  | readonly AnalyticsPrimitive[];
export type AnalyticsProperties = Record<string, AnalyticsPropertyValue>;

const PROPERTY_KEY = /^[a-z][a-z0-9_]{0,63}$/;
const MAX_PROPERTIES = 40;
const MAX_STRING_LENGTH = 240;
const MAX_ARRAY_LENGTH = 50;
const BLOCKED_PROPERTY_FRAGMENTS = [
  "email",
  "phone",
  "password",
  "secret",
  "token",
  "first_name",
  "last_name",
  "full_name",
  "contact_name",
  "company_name",
  "user_id",
  "distinct_id",
  "route_trace",
  "gps_trace",
] as const;
const BLOCKED_PROPERTY_KEYS = new Set([
  "lat",
  "lng",
  "latitude",
  "longitude",
  "coordinates",
]);

export function isAnalyticsEventName(value: string): value is AnalyticsEventName {
  return Object.prototype.hasOwnProperty.call(ANALYTICS_EVENT_REGISTRY, value);
}

export function sanitiseAnalyticsProperties(
  rawProperties: Record<string, unknown> | undefined,
): AnalyticsProperties {
  if (!rawProperties) return {};

  const properties: AnalyticsProperties = {};
  for (const [rawKey, value] of Object.entries(rawProperties)) {
    if (Object.keys(properties).length >= MAX_PROPERTIES) break;
    const key = normaliseAnalyticsPropertyKey(rawKey);
    if (!PROPERTY_KEY.test(key)) continue;
    if (BLOCKED_PROPERTY_KEYS.has(key)) continue;
    if (BLOCKED_PROPERTY_FRAGMENTS.some((fragment) => key.includes(fragment))) {
      continue;
    }

    const sanitised = sanitiseAnalyticsPropertyValue(value);
    if (sanitised !== undefined) properties[key] = sanitised;
  }
  return properties;
}

function normaliseAnalyticsPropertyKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}

export function prepareAnalyticsProperties(
  event: AnalyticsEventName,
  rawProperties: Record<string, unknown> | undefined,
  source: AnalyticsSource,
): AnalyticsProperties {
  const definition = ANALYTICS_EVENT_REGISTRY[event];
  return {
    ...sanitiseAnalyticsProperties(rawProperties),
    analytics_area: definition.area,
    analytics_environment: "production",
    analytics_schema_version: definition.version,
    analytics_source: source,
  };
}

function sanitiseAnalyticsPropertyValue(
  value: unknown,
): AnalyticsPropertyValue | undefined {
  if (value === null || typeof value === "boolean") return value;
  if (typeof value === "string") return value.slice(0, MAX_STRING_LENGTH);
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (!Array.isArray(value)) return undefined;

  const safeValues: AnalyticsPrimitive[] = [];
  for (const item of value.slice(0, MAX_ARRAY_LENGTH)) {
    if (item === null || typeof item === "boolean") {
      safeValues.push(item);
    } else if (typeof item === "string") {
      safeValues.push(item.slice(0, MAX_STRING_LENGTH));
    } else if (typeof item === "number" && Number.isFinite(item)) {
      safeValues.push(item);
    }
  }
  return safeValues;
}
